import { useEffect, useRef } from "react";
import { FormikProps } from "formik";

import { useLatest } from "../useLatest";

import { LOGIN_OTP_LENGTH } from "./constants";

interface UseOtpAutoSubmitOptions<T extends { otp?: string }> {
    formik: FormikProps<T>;
    onSubmit: () => void | Promise<void>;
}

/** Submits the login form once when a valid OTP is entered; allows retry only after OTP value changes. */
export function useOtpAutoSubmit<T extends { otp?: string }>({ formik, onSubmit }: UseOtpAutoSubmitOptions<T>) {
    const lastSubmittedOtp = useRef<string | null>(null);
    const submitInFlightRef = useRef(false);
    const onSubmitRef = useLatest(onSubmit);

    useEffect(() => {
        const otp = formik.values.otp;

        if (
            (otp?.length ?? 0) !== LOGIN_OTP_LENGTH ||
            !formik.isValid ||
            formik.isSubmitting ||
            submitInFlightRef.current ||
            lastSubmittedOtp.current === otp
        ) {
            return;
        }

        lastSubmittedOtp.current = otp ?? null;
        submitInFlightRef.current = true;

        void Promise.resolve(onSubmitRef.current()).finally(() => {
            submitInFlightRef.current = false;
        });
    }, [formik.isSubmitting, formik.isValid, formik.values.otp, onSubmitRef]);

    const resetAutoSubmit = () => {
        lastSubmittedOtp.current = null;
    };

    return { resetAutoSubmit };
}
